import { addDays, endOfDay, format } from 'date-fns';
import type {
	AccessEventDetail,
	GuestAccessGrantDetail,
	PropertyOwnerHistoryFull,
	SharingUser,
} from '@/client/types.gen';
import { getRoleLabel, Roles, UserStatuses } from '@/utils/roleUtils';
import { getUserDisplayName } from '@/utils/userDisplayUtils';
import { parseIsoToDate } from '@/utils/dateUtils';

export const DefaultActions = {
	Approve: 'approve',
	Deny: 'deny',
} as const;
export type DefaultAction = (typeof DefaultActions)[keyof typeof DefaultActions];

export const GrantStatuses = {
	Pending: 'pending',
	Approved: 'approved',
	Denied: 'denied',
	Expired: 'expired',
	Revoked: 'revoked',
} as const;
export type GrantStatus = (typeof GrantStatuses)[keyof typeof GrantStatuses];

export const GrantActivityKinds = {
	Requested: 'requested',
	Invited: 'invited',
	Accepted: 'accepted',
	// Legacy grant-merge timeline (buildAccessActivityItems) label; distinct from the activity-log
	// `Accepted` kind above. Retained for the fallback path used by synthetic guest grants.
	AcceptedInvite: 'accepted-invite',
	Approved: 'approved',
	Joined: 'joined',
	Extended: 'extended',
	Reduced: 'reduced',
	RoleChanged: 'role-changed',
	Denied: 'denied',
	Expired: 'expired',
	Revoked: 'revoked',
} as const;
export type GrantActivityKind = (typeof GrantActivityKinds)[keyof typeof GrantActivityKinds];

export type GrantActivityItem = {
	kind: GrantActivityKind;
	title: string;
	date?: string | null;
	detail?: string;
	role?: string;
	ongoing?: boolean;
	fallback?: string;
	/** True when the row is copied history from another property (professional archive). */
	cloned?: boolean;
};

export type AccessTransferRecord = Pick<PropertyOwnerHistoryFull, 'transferred_at' | 'cloned_house_id'>;

/**
 * The fixed access-activity vocabulary emitted by the per-user log. Distinct from the grant
 * `status` strings above — an event is a point-in-time action, a status is the current state.
 */
export const AccessEvents = {
	Requested: 'access_requested',
	Invited: 'invited',
	Accepted: 'accepted',
	Approved: 'approved',
	Joined: 'joined',
	Connected: 'connected',
	Denied: 'denied',
	ExpiryChanged: 'expiry_changed',
	RoleChanged: 'role_changed',
	Revoked: 'revoked',
	Expired: 'expired',
	Login: 'login',
} as const;

const TRANSFER_ACCESS_END_WINDOW_MS = 15 * 60 * 1000;
const MUTABLE_ACCESS_EDIT_WINDOW_MS = 10 * 60 * 1000;
const END_OF_DAY_EXPIRY_HOUR = 23;
const END_OF_DAY_EXPIRY_MINUTE_THRESHOLD = 55;

const readMetaString = (meta: AccessEventDetail['meta'], key: string): string | undefined => {
	const value = meta?.[key];
	return typeof value === 'string' ? value : undefined;
};

const readMetaTime = (meta: AccessEventDetail['meta'], key: string): number | null => {
	const value = readMetaString(meta, key);
	if (!value) return null;
	const time = new Date(value).getTime();
	return Number.isNaN(time) ? null : time;
};

// Extend vs shorten is no longer a distinct event — both arrive as `expiry_changed`. Prefer the
// explicit `action_type` when present, otherwise derive direction from the expiry timestamps
// (a null/absent expiry means indefinite, i.e. the longer end of the scale).
const isExpiryShortened = (meta: AccessEventDetail['meta']): boolean => {
	const actionType = readMetaString(meta, 'action_type');
	if (actionType === 'extend') return false;
	if (actionType === 'reduce' || actionType === 'shorten') return true;

	const previous = readMetaTime(meta, 'previous_expires_at');
	const next = readMetaTime(meta, 'new_expires_at');
	if (next === null) return false; // became indefinite → longer
	if (previous === null) return true; // gained a limit → shorter
	return next < previous;
};

const roleChangedTitle = (meta: AccessEventDetail['meta']): string => {
	const toRole = readMetaString(meta, 'to_role');
	return toRole ? `Role changed to ${getRoleLabel(toRole)}` : 'Role changed';
};

const roleChangedTargetRole = (meta: AccessEventDetail['meta']): string | undefined => readMetaString(meta, 'to_role');

/**
 * What an `invited` row is called, on the Access drawer and the contact drawer alike.
 *
 * `meta.resend` distinguishes the first invitation from a later one; the role is read from the
 * event where it carries one, so a row keeps saying what was granted after the role has changed.
 */
export const invitedTitle = (meta: AccessEventDetail['meta'], fallbackRole?: string): string => {
	const role =
		readMetaString(meta, 'role') ??
		readMetaString(meta, 'to_role') ??
		readMetaString(meta, 'new_role') ??
		fallbackRole;
	const invited = meta?.resend === true ? 'Re-invited' : 'Invited';
	return role ? `${invited} as ${getRoleLabel(role)}` : invited;
};

const isEndOfDayExpiry = (date: Date): boolean =>
	date.getHours() === END_OF_DAY_EXPIRY_HOUR && date.getMinutes() >= END_OF_DAY_EXPIRY_MINUTE_THRESHOLD;

const formatActivityExpiryTarget = (date: Date): string => {
	const dateFormat = date.getFullYear() === new Date().getFullYear() ? 'd MMM' : 'd MMM yyyy';
	return isEndOfDayExpiry(date)
		? `end of ${format(date, dateFormat)}`
		: `${format(date, dateFormat)}, ${format(date, 'h:mm a')}`;
};

const expiryChangedTitle = (meta: AccessEventDetail['meta'], shortened: boolean): string => {
	const fallback = shortened ? 'Access shortened' : 'Access extended';
	const value = meta?.new_expires_at;
	if (value === null) return 'Access made ongoing';
	if (typeof value !== 'string') return fallback;

	const date = parseIsoToDate(value);
	if (!date) return fallback;
	return `${fallback} to ${formatActivityExpiryTarget(date)}`;
};

const expiryChangedMadeOngoing = (meta: AccessEventDetail['meta']): boolean => meta?.new_expires_at === null;

const hasTransferCauseMeta = (meta: AccessEventDetail['meta']): boolean => {
	const cause = readMetaString(meta, 'reason') ?? readMetaString(meta, 'cause') ?? readMetaString(meta, 'source');
	return cause === 'property_transfer' || cause === 'transfer' || cause === 'ownership_transfer';
};

/**
 * Parses cloned-transfer timestamps to numbers once per activity build, so revoked/expired events
 * compare numbers instead of re-parsing every transfer date per event (events × transfers parses).
 */
export const getClonedOwnershipTransferTimes = (ownershipTransfers: AccessTransferRecord[] = []): number[] => {
	const transferTimes: number[] = [];
	for (const transfer of ownershipTransfers) {
		if (transfer.cloned_house_id == null) continue;
		const transferTime = new Date(transfer.transferred_at).getTime();
		if (!Number.isNaN(transferTime)) transferTimes.push(transferTime);
	}
	return transferTimes;
};

const isNearClonedTransfer = (date: string | null | undefined, transferTimes: readonly number[]): boolean => {
	if (!date) return false;
	const eventTime = new Date(date).getTime();
	if (Number.isNaN(eventTime)) return false;

	return transferTimes.some((transferTime) => Math.abs(eventTime - transferTime) <= TRANSFER_ACCESS_END_WINDOW_MS);
};

export const accessEndTitle = (
	kind: GrantActivityKind,
	date: string | null | undefined,
	options: { meta?: AccessEventDetail['meta']; ownershipTransferTimes?: readonly number[] } = {},
): string | undefined => {
	const canBeTransferEnd = kind === GrantActivityKinds.Revoked || kind === GrantActivityKinds.Expired;
	if (
		canBeTransferEnd &&
		(hasTransferCauseMeta(options.meta) || isNearClonedTransfer(date, options.ownershipTransferTimes ?? []))
	) {
		return 'Access ended during transfer';
	}
	if (kind === GrantActivityKinds.Revoked) return 'Access removed';
	if (kind === GrantActivityKinds.Expired) return 'Access expired';
	return undefined;
};

const isMutableAccessEdit = (item: GrantActivityItem): boolean =>
	item.kind === GrantActivityKinds.RoleChanged ||
	item.kind === GrantActivityKinds.Extended ||
	item.kind === GrantActivityKinds.Reduced;

const mutableAccessEditCategory = (item: GrantActivityItem): 'role' | 'expiry' | null => {
	if (item.kind === GrantActivityKinds.RoleChanged) return 'role';
	if (item.kind === GrantActivityKinds.Extended || item.kind === GrantActivityKinds.Reduced) return 'expiry';
	return null;
};

const getActivityTime = (item: GrantActivityItem): number | null => {
	if (!item.date) return null;
	const time = new Date(item.date).getTime();
	return Number.isNaN(time) ? null : time;
};

const withinMutableEditWindow = (newer: GrantActivityItem, older: GrantActivityItem): boolean => {
	const newerTime = getActivityTime(newer);
	const olderTime = getActivityTime(older);
	if (newerTime === null || olderTime === null) return false;
	return Math.abs(newerTime - olderTime) <= MUTABLE_ACCESS_EDIT_WINDOW_MS;
};

const compactMutableAccessEdits = (items: GrantActivityItem[]): GrantActivityItem[] => {
	const compacted: GrantActivityItem[] = [];

	for (let index = 0; index < items.length; index += 1) {
		const item = items[index];
		if (!isMutableAccessEdit(item)) {
			compacted.push(item);
			continue;
		}

		const session = [item];
		let cursor = index + 1;
		while (
			cursor < items.length &&
			isMutableAccessEdit(items[cursor]) &&
			withinMutableEditWindow(session[session.length - 1], items[cursor])
		) {
			session.push(items[cursor]);
			cursor += 1;
		}

		const seenCategories = new Set<string>();
		session.forEach((sessionItem) => {
			const category = mutableAccessEditCategory(sessionItem);
			if (!category || seenCategories.has(category)) return;
			seenCategories.add(category);
			compacted.push(sessionItem);
		});

		index = cursor - 1;
	}

	return compacted;
};

const mapAccessEventToActivityItem = (
	event: AccessEventDetail,
	options: {
		invitedRoleFallback?: string;
		ownershipTransferTimes?: readonly number[];
	} = {},
): GrantActivityItem | null => {
	const cloned = event.cloned_from_house_id != null;
	const item = (kind: GrantActivityKind, title: string, detail?: string, role?: string): GrantActivityItem => ({
		kind,
		title,
		date: event.ts,
		...(detail ? { detail } : {}),
		...(role ? { role } : {}),
		cloned,
	});

	switch (event.event) {
		case AccessEvents.Requested:
			return item(GrantActivityKinds.Requested, 'Access requested');
		case AccessEvents.Invited:
			return item(GrantActivityKinds.Invited, invitedTitle(event.meta, options.invitedRoleFallback));
		case AccessEvents.Accepted:
			return item(GrantActivityKinds.Accepted, 'Invitation accepted');
		case AccessEvents.Approved:
			return item(GrantActivityKinds.Approved, 'Access approved');
		case AccessEvents.Joined:
			return item(GrantActivityKinds.Joined, 'Joined this property');
		case AccessEvents.Connected:
			// A contact card switched on. The grant it makes is the one the invitation that follows
			// records as "Invited as ...", the same as a grant made from this page, so a line here
			// would say it twice. The contacts rail is where the connection itself is drawn.
			return null;
		case AccessEvents.Denied:
			return item(GrantActivityKinds.Denied, 'Access denied');
		case AccessEvents.ExpiryChanged: {
			const shortened = isExpiryShortened(event.meta);
			const expiryItem = shortened
				? item(GrantActivityKinds.Reduced, expiryChangedTitle(event.meta, shortened))
				: item(GrantActivityKinds.Extended, expiryChangedTitle(event.meta, shortened));
			return expiryChangedMadeOngoing(event.meta) ? { ...expiryItem, ongoing: true } : expiryItem;
		}
		case AccessEvents.RoleChanged:
			return item(
				GrantActivityKinds.RoleChanged,
				roleChangedTitle(event.meta),
				undefined,
				roleChangedTargetRole(event.meta),
			);
		case AccessEvents.Revoked:
			return item(
				GrantActivityKinds.Revoked,
				accessEndTitle(GrantActivityKinds.Revoked, event.ts, {
					meta: event.meta,
					ownershipTransferTimes: options.ownershipTransferTimes,
				}) ?? 'Access removed',
			);
		case AccessEvents.Expired:
			return item(
				GrantActivityKinds.Expired,
				accessEndTitle(GrantActivityKinds.Expired, event.ts, {
					meta: event.meta,
					ownershipTransferTimes: options.ownershipTransferTimes,
				}) ?? 'Access expired',
			);
		case AccessEvents.Login:
			return null;
		default:
			return null;
	}
};

/**
 * When access ran out without the log saying so: the account's end date, once it has passed, or
 * null. Nothing writes an event when a date arrives, so the log alone stops at the change that set
 * it. Shared by every timeline that reads the log, so they agree on whether access has ended.
 *
 * Null when the log already ends in a removal or an expiry, and when anything was logged after the
 * date, since that superseded it and the expiry never happened. Null for a log with nothing in it
 * either: an ending with no beginning above it is not a history.
 *
 * Read against the clock when a timeline is built, not kept live: a drawer left open across the
 * date shows the expiry on its next build, the same moment the lists behind it move the person.
 */
export const unloggedExpiryAt = (
	events: readonly AccessEventDetail[],
	expiresAt: string | null | undefined,
): string | null => {
	// TODO(access-activity): have the backend write an `expired` event when `expires_at` passes, from
	// a scheduled job or the first read after it. Both timelines then draw that row, and this goes.
	if (!expiresAt) return null;
	const expiryTime = new Date(expiresAt).getTime();
	if (Number.isNaN(expiryTime) || expiryTime >= Date.now()) return null;

	let newest: AccessEventDetail | undefined;
	for (const event of events) {
		if (event.event === AccessEvents.Login) continue;
		if (!newest || new Date(event.ts).getTime() > new Date(newest.ts).getTime()) newest = event;
	}
	if (!newest) return null;
	if (newest.event === AccessEvents.Revoked || newest.event === AccessEvents.Expired) return null;
	return expiryTime < new Date(newest.ts).getTime() ? null : expiresAt;
};

/**
 * Builds the drawer timeline from a user's access-activity log. The log is per-user (a merged
 * view across every grant/association), newest first; unknown event kinds are dropped.
 *
 * `expiresAt` is the user's saved end date, drawn as the access ending once it has passed (see
 * `unloggedExpiryAt`).
 */
export const buildActivityItemsFromEvents = (
	events: AccessEventDetail[],
	options: {
		invitedRoleFallback?: string;
		ownershipTransfers?: AccessTransferRecord[];
		expiresAt?: string | null;
	} = {},
): GrantActivityItem[] => {
	const hasRoleChange = events.some((event) => event.event === AccessEvents.RoleChanged);
	const ownershipTransferTimes = getClonedOwnershipTransferTimes(options.ownershipTransfers);
	// TODO(access-activity): have the backend include the invited role on invited/re-invited
	// event metadata. The current fallback role is only safe while no role-change event exists.
	const eventOptions = {
		ownershipTransferTimes,
		invitedRoleFallback: hasRoleChange ? undefined : options.invitedRoleFallback,
	};

	const mappedItems = [...events]
		.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
		.map((event) => mapAccessEventToActivityItem(event, eventOptions))
		.filter((item): item is GrantActivityItem => item !== null);

	const items = compactMutableAccessEdits(mappedItems);
	// An empty timeline is left empty, so the drawer still falls back to the grant-derived one.
	const expiredAt = items.length > 0 ? unloggedExpiryAt(events, options.expiresAt) : null;
	if (!expiredAt) return items;

	const expired: GrantActivityItem = {
		kind: GrantActivityKinds.Expired,
		title: accessEndTitle(GrantActivityKinds.Expired, expiredAt, { ownershipTransferTimes }) ?? 'Access expired',
		date: expiredAt,
	};
	return [expired, ...items];
};

export const Durations = {
	Today: 'today',
	Tomorrow: 'tomorrow',
	Ongoing: 'ongoing',
} as const;
export type DurationKey = (typeof Durations)[keyof typeof Durations];

export const getExpiresAt = (duration: DurationKey): Date | null => {
	if (duration === Durations.Today) return endOfDay(new Date());
	if (duration === Durations.Tomorrow) return endOfDay(addDays(new Date(), 1));
	return null;
};

export const formatAccessEnds = (duration: DurationKey): string => {
	const date = getExpiresAt(duration);
	if (!date) return 'No expiry';
	return format(date, 'd MMM, HH:mm');
};

export const isDeniedGrant = (grant: GuestAccessGrantDetail) => grant.status === GrantStatuses.Denied;
export const isRevokedGrant = (grant: GuestAccessGrantDetail) => grant.status === GrantStatuses.Revoked;
export const isExpiredGrant = (grant: GuestAccessGrantDetail) => grant.status === GrantStatuses.Expired;
export const isApprovedGrant = (grant: GuestAccessGrantDetail) => grant.status === GrantStatuses.Approved;
export const getGrantActivityAt = (grant: GuestAccessGrantDetail) => grant.updated_at ?? grant.created_at;

export const buildGrantActivityItems = (grant: GuestAccessGrantDetail): GrantActivityItem[] => {
	const items: GrantActivityItem[] = [
		{
			kind: GrantActivityKinds.Requested,
			title: 'Requested',
			date: grant.created_at,
		},
	];

	if (grant.approved_at) {
		items.push({
			kind: GrantActivityKinds.Approved,
			title: 'Approved',
			date: grant.approved_at,
		});
	}

	if (isDeniedGrant(grant)) {
		items.push({
			kind: GrantActivityKinds.Denied,
			title: 'Denied',
			date: grant.updated_at,
			fallback: 'Access request denied',
		});
	}

	if (isRevokedGrant(grant)) {
		items.push({
			kind: GrantActivityKinds.Revoked,
			title: 'Removed',
			date: grant.updated_at,
			fallback: 'Access removed',
		});
	}

	if (isExpiredGrant(grant)) {
		items.push({
			kind: GrantActivityKinds.Expired,
			title: 'Expired',
			date: grant.expires_at ?? grant.updated_at,
			fallback: 'Access expired',
		});
	}

	return items;
};

const getDeniedGrantUserId = (grantId: string) =>
	-Array.from(grantId).reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) % 1_000_000_000, 7);

export const grantToSharingUser = (grant: GuestAccessGrantDetail): SharingUser => ({
	user_id: getDeniedGrantUserId(grant.id),
	name: getUserDisplayName(grant.requester ?? null) || null,
	email: grant.requester?.email ?? '',
	role: Roles.Viewer,
	status: UserStatuses.Registered,
	access_purpose: grant.purpose ?? null,
	expires_at: null,
});
